/**
 * Ajustes de la plataforma que decide quien la gestiona.
 *
 * Son pocos y casi siempre hacen falta a la vez, asi que van en una
 * sola consulta. Cada uno con una reserva sensata por si la consulta
 * falla: la plataforma tiene que seguir funcionando.
 */

import { useQuery } from '@tanstack/react-query'
import { getSupabase, isSupabaseEnabled } from '@/lib/supabase'

export type SalesMode = 'b2c' | 'b2b' | 'both'

export interface PlatformSettings {
  salesMode: SalesMode
  /** Si hay que hacer el diagnostico para abrir el curso. */
  diagnosticRequired: boolean
}

const TENANT = 'mentor-wallet'

/**
 * Lo que se asume si la consulta no responde.
 *
 * B2B desde D-366, igual que el valor por defecto de la columna en la
 * base de datos. Importa que sea este y no b2c: si la consulta falla,
 * el respaldo decide si se ensena el checkout a un particular. Errar
 * hacia b2b deja una puerta de menos abierta, que es el lado seguro.
 */
const POR_DEFECTO: PlatformSettings = {
  salesMode: 'b2b',
  diagnosticRequired: true,
}

async function fetchSettings(): Promise<PlatformSettings> {
  if (!isSupabaseEnabled()) return POR_DEFECTO
  const supabase = getSupabase()
  if (!supabase) return POR_DEFECTO

  const { data, error } = await supabase.rpc('get_platform_settings', {
    p_tenant_slug: TENANT,
  })

  if (error || !data || data.length === 0) {
    if (error) console.info('[settings]', error.message)
    return POR_DEFECTO
  }

  const r = data[0] as Record<string, unknown>
  return {
    salesMode: (r.sales_mode as SalesMode) ?? 'b2b',
    diagnosticRequired: r.diagnostic_required !== false,
  }
}

export function usePlatformSettings(): PlatformSettings & {
  /** Si un particular puede comprar por su cuenta. */
  canBuyDirect: boolean
  /** Si se ofrece la via de empresas. */
  showsBusiness: boolean
  isLoading: boolean
} {
  const { data = POR_DEFECTO, isLoading } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: fetchSettings,
    // Cambian muy de vez en cuando
    staleTime: 10 * 60 * 1000,
  })

  return {
    ...data,
    canBuyDirect: data.salesMode === 'b2c' || data.salesMode === 'both',
    showsBusiness: data.salesMode === 'b2b' || data.salesMode === 'both',
    isLoading,
  }
}

/** Cambia el modo de venta. Solo funciona si quien llama es admin. */
export async function setSalesMode(mode: SalesMode): Promise<boolean> {
  const supabase = getSupabase()
  if (!supabase) return false

  const { error } = await supabase.rpc('set_sales_mode', {
    p_tenant_slug: TENANT,
    p_mode: mode,
  })
  if (error) {
    console.error('[settings] setSalesMode:', error.message)
    return false
  }
  return true
}

/** Cambia si el diagnostico bloquea el curso. */
export async function setDiagnosticRequired(required: boolean): Promise<boolean> {
  const supabase = getSupabase()
  if (!supabase) return false

  const { error } = await supabase.rpc('set_diagnostic_required', {
    p_tenant_slug: TENANT,
    p_required: required,
  })
  if (error) {
    console.error('[settings] setDiagnosticRequired:', error.message)
    return false
  }
  return true
}
